/**
 * Modal — a compatibility shim over <Sheet>.
 *
 * This used to be a second Radix Dialog wrapper with its own backdrop, its own
 * ~24px close button and `sm:rounded-2xl`, which meant its 13 consumers
 * rendered sharp-cornered and full-bleed on every phone. Rather than churn
 * those 13 files, the exports below now delegate to sheet.tsx: one backdrop,
 * one 44px close affordance, one bottom-sheet-on-mobile presentation, and one
 * set of Radix behaviours. No consumer changed; every consumer got the fixes.
 *
 * Only the chassis moved. `ModalContent` still applies the layout the existing
 * consumers were written against — `grid gap-4 p-6 max-w-lg` — so their
 * spacing and their `className` overrides behave exactly as before.
 *
 * New overlays should import from `./sheet` directly. This file exists so that
 * "migrate the last 13" is a follow-up chore rather than a prerequisite, and
 * so there are never two competing primitives — there is one, plus an alias.
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  type SheetContentProps,
} from './sheet';

const Modal = Sheet;
const ModalTrigger = SheetTrigger;
const ModalClose = SheetClose;
const ModalPortal = SheetPortal;
const ModalOverlay = SheetOverlay;

const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, hasDescription = true, ...props }, ref) => (
  // `hasDescription` defaults to true here, not false as on SheetContent:
  // nine of the thirteen consumers render a <ModalDescription>, and Radix's
  // aria-describedby wiring is what links it. Parity beats tidiness.
  <SheetContent
    ref={ref}
    side="center"
    hasDescription={hasDescription}
    className={cn('grid gap-4 p-6 max-w-lg', className)}
    {...props}
  />
));
ModalContent.displayName = DialogPrimitive.Content.displayName;

const ModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
ModalHeader.displayName = 'ModalHeader';

const ModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
ModalFooter.displayName = 'ModalFooter';

const ModalTitle = React.forwardRef<
  React.ElementRef<typeof SheetTitle>,
  React.ComponentPropsWithoutRef<typeof SheetTitle>
>(({ className, ...props }, ref) => (
  <SheetTitle
    ref={ref}
    className={cn(
      'text-lg font-bold leading-none tracking-tight text-cream',
      className
    )}
    {...props}
  />
));
ModalTitle.displayName = DialogPrimitive.Title.displayName;

const ModalDescription = React.forwardRef<
  React.ElementRef<typeof SheetDescription>,
  React.ComponentPropsWithoutRef<typeof SheetDescription>
>(({ className, ...props }, ref) => (
  <SheetDescription
    ref={ref}
    className={cn('text-sm text-warm-gray', className)}
    {...props}
  />
));
ModalDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Modal,
  ModalPortal,
  ModalOverlay,
  ModalTrigger,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
};
